import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import {
  Box,
  FormControl,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchDepletionsPremisesAccounts,
  useDepletionsByState,
  useDepletionsPremisesAccounts,
  useDepletionsPremisesSummary,
} from '../api/depletions';
import type {
  OnlineRetailerAccount,
  PremisesAccount,
  PremisesAccountSort,
  PremisesAccountsQuery,
  PremisesBucket,
  PremisesSummaryResponse,
  PremisesYearlyBucket,
} from '../api/depletions';
import { shortProductLabel } from '../lib/products';
import { fmt6PkNumber, toSixPacks } from '../lib/volume';
import { colors } from '../theme';
import {
  accountColumns,
  excelSheet,
  isoDate,
  num,
  premisesLabel,
  type ExcelWorkbookSpec,
  type ExportFilter,
} from '../util/excel-export';
import { SectionExplainer } from './SectionExplainer';
import { useSectionExport } from '../hooks/useSectionExport';

const fmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const fmtInt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// Violet — distinct from OFF (blue), ON (green), Other (amber). Marks
// the online-retailer e-commerce channel across the tiles + YoY panels.
const ONLINE_COLOR = '#7c5cc4';

// Alcohol Beverage Control ("control") states for spirits — markets where
// the state, not private distributors, controls distribution / retail. The
// distribution model differs, so the state filter flags them. Standard
// 17-state spirits list; adjust here if HY tracks a different set.
const CONTROL_STATES = new Set<string>([
  'AL',
  'ID',
  'IA',
  'ME',
  'MI',
  'MS',
  'MT',
  'NH',
  'NC',
  'OH',
  'OR',
  'PA',
  'UT',
  'VT',
  'VA',
  'WV',
  'WY',
]);
const CONTROL_COLOR = '#4f6bb0';

// Small "Control" pill — reused in the dropdown options, the selected
// value, and the filter-bar legend so the mark reads the same everywhere.
function ControlChip() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.6,
        py: 0.1,
        borderRadius: 999,
        fontSize: 9.5,
        fontWeight: 800,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: CONTROL_COLOR,
        bgcolor: `${CONTROL_COLOR}14`,
        border: `1px solid ${CONTROL_COLOR}44`,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      Control
    </Box>
  );
}

interface TileStyle {
  label: string;
  description: string;
  color: string;
  bg: string;
  icon: ReactNode;
  // What it actually means in plain English — surfaces as a hover.
  meaning: string;
}

const TILE_STYLE: Record<string, TileStyle> = {
  OFF: {
    label: 'Off-Premise',
    description: 'Liquor stores · retail',
    color: colors.info,
    bg: `${colors.info}10`,
    icon: <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />,
    meaning:
      'Accounts where bottles are sold for consumption elsewhere — package stores, retail chains. Where the bulk of HY volume lives.',
  },
  ON: {
    label: 'On-Premise',
    description: 'Bars · restaurants',
    color: colors.success,
    bg: `${colors.success}10`,
    icon: <RestaurantIcon sx={{ fontSize: 18 }} />,
    meaning:
      'Accounts where bottles are poured on site — bars, restaurants, lounges. Lower volume per account but higher brand visibility.',
  },
  OTHER: {
    label: 'Other',
    description: 'NA + Unknown',
    color: colors.warning,
    bg: `${colors.warning}10`,
    icon: <LocalBarIcon sx={{ fontSize: 18 }} />,
    meaning:
      'Accounts that are neither on- nor off-premise — broker-classified NA (clubs, military exchanges, cigar lounges) plus older accounts still awaiting premises classification.',
  },
  ONLINE: {
    label: 'Online Retailers',
    description: 'E-commerce channel',
    color: ONLINE_COLOR,
    bg: `${ONLINE_COLOR}10`,
    icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />,
    meaning:
      'A curated set of e-commerce / online retail accounts, pulled out of Off-Premise so their direct-to-consumer volume is tracked as its own channel. Hover for the accounts inside.',
  },
};

function styleFor(premises: string | null): TileStyle {
  if (premises === 'OFF') return TILE_STYLE.OFF!;
  if (premises === 'ON') return TILE_STYLE.ON!;
  if (premises === 'ONLINE') return TILE_STYLE.ONLINE!;
  return TILE_STYLE.OTHER!;
}

export function DepletionsPremisesMix() {
  // '' == all states. Drives the state filter for the whole section.
  const [state, setState] = useState<string>('');
  // Which tile is expanded to show its underlying accounts (null == none).
  const [openBucket, setOpenBucket] = useState<string | null>(null);
  const { data, isLoading, error } = useDepletionsPremisesSummary(undefined, state || null);

  // Changing the state filter closes any open account drill-down so we
  // never show a bucket that the new state may not even have.
  const selectState = (next: string) => {
    setState(next);
    setOpenBucket(null);
  };

  // States for the filter dropdown — reuse the by-state breakdown so we
  // only offer states that actually have accounts. Sorted alphabetically.
  const { data: stateData } = useDepletionsByState();
  const stateOptions = useMemo<string[]>(() => {
    if (!stateData) return [];
    return stateData.states
      .map((s) => s.state_code)
      .filter((c): c is string => !!c)
      .sort();
  }, [stateData]);

  // Collapse NA + Unknown into a single "Other" bucket for display —
  // on/off are the story; everything else is one catch-all. Done at the
  // display layer so the backend contract stays untouched.
  const mergedBuckets = useMemo<PremisesBucket[]>(() => {
    if (!data) return [];
    const grand = Number(data.grand_total_9l) || 0;
    let on: PremisesBucket | null = null;
    let off: PremisesBucket | null = null;
    let online: PremisesBucket | null = null;
    let otherAccounts = 0;
    let other9l = 0;
    for (const b of data.buckets) {
      if (b.premises_type === 'ON') on = b;
      else if (b.premises_type === 'OFF') off = b;
      else if (b.premises_type === 'ONLINE') online = b;
      else {
        // NA + Unknown collapse into "Other". ONLINE is handled above
        // so it stays its own channel rather than joining the catch-all.
        otherAccounts += b.account_count;
        other9l += Number(b.total_9l);
      }
    }
    const out: PremisesBucket[] = [];
    if (off) out.push(off);
    if (on) out.push(on);
    if (otherAccounts > 0 || other9l > 0) {
      out.push({
        premises_type: 'OTHER',
        account_count: otherAccounts,
        total_9l: String(other9l),
        pct_of_9l: grand > 0 ? other9l / grand : 0,
      });
    }
    // Online retailers as the 4th tile — pulled out of OFF above.
    if (online) out.push(online);
    return out;
  }, [data]);

  // The two sub-parts folded into "Other" — kept so the tile can show
  // the NA vs Unknown split rather than hiding it behind one label.
  const otherBreakdown = useMemo<OtherBreakdown | null>(() => {
    if (!data) return null;
    let naAccts = 0;
    let naVol = 0;
    let unkAccts = 0;
    let unkVol = 0;
    for (const b of data.buckets) {
      if (b.premises_type === 'NA') {
        naAccts += b.account_count;
        naVol += Number(b.total_9l);
      } else if (b.premises_type !== 'ON' && b.premises_type !== 'OFF') {
        unkAccts += b.account_count;
        unkVol += Number(b.total_9l);
      }
    }
    return { naAccts, naVol, unkAccts, unkVol };
  }, [data]);

  // The open drill-down's search / sort / active-only, reported up by the
  // panel so the export can fetch every matching account, not the top 100.
  const panelQueryRef = useRef<PanelQuery | null>(null);
  const onPanelQueryChange = useCallback((q: PanelQuery | null) => {
    panelQueryRef.current = q;
  }, []);

  useSectionExport(async () => {
    if (!data) return null;
    const panelQuery = panelQueryRef.current;
    const drill =
      openBucket && panelQuery
        ? {
            bucket: openBucket,
            accounts: (
              await fetchDepletionsPremisesAccounts(openBucket, {
                ...panelQuery,
                state: state || null,
                limit: 5000,
              })
            ).accounts,
          }
        : null;
    const filters: ExportFilter[] = [
      { label: 'State', value: state || 'All states' },
      {
        label: 'Drill-down bucket',
        value: drill ? `${styleFor(drill.bucket).label} accounts` : 'None open',
      },
      ...(drill && panelQuery
        ? [
            {
              label: 'Drill-down search (account or city)',
              value: panelQuery.search ? `"${panelQuery.search}"` : '',
            },
            { label: 'Drill-down active only', value: panelQuery.activeOnly ? 'Yes' : 'No' },
            {
              kind: 'sort' as const,
              label: 'Drill-down sorted by',
              value: PANEL_SORT_EXPORT_LABEL[panelQuery.sort ?? 'volume'],
            },
          ]
        : []),
      { kind: 'info', label: 'Data through', value: data.reference_date },
    ];
    return buildPremisesExport(data, mergedBuckets, drill, filters);
  }, !!data);

  if (error) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography sx={{ color: colors.error, fontSize: 13 }}>
          Failed to load premises mix.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
            Premises Mix
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: colors.textMuted, mt: 0.25 }}>
            Where Hooten Young ends up being sold — off-premise (liquor stores, retail) vs
            on-premise (bars, restaurants) vs everything else (Other), with online retailers pulled
            out as their own e-commerce channel.
          </Typography>
        </Box>
        {data ? (
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              sx={{
                fontSize: 11,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {state ? `Total · ${state}` : 'Total'}
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.textPrimary,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {fmtInt.format(data.grand_total_accounts)} accounts ·{' '}
              {fmt6PkNumber(data.grand_total_9l)} 6-pk · {fmt.format(Number(data.grand_total_9l))}{' '}
              9L
            </Typography>
          </Box>
        ) : null}
      </Box>

      {/* Filter bar — scope the whole section by state. Matches the
          labeled-control pattern used by the other depletions filters. */}
      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 1.25,
          borderRadius: 2,
          border: `1px solid ${colors.borderLight}`,
          bgcolor: colors.surfaceSubtle,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mr: 0.5 }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 16, color: colors.textMuted }} />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            State
          </Typography>
        </Box>
        <FormControl size="small">
          <Select
            value={state}
            onChange={(e) => selectState(e.target.value)}
            displayEmpty
            aria-label="Filter premises mix by state"
            renderValue={(val) =>
              val ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  {val}
                  {CONTROL_STATES.has(val) ? <ControlChip /> : null}
                </Box>
              ) : (
                'All states'
              )
            }
            sx={{
              minWidth: 150,
              fontSize: 13,
              bgcolor: '#fff',
              '& .MuiSelect-select': { py: 0.6 },
              '& fieldset': { borderColor: colors.border },
            }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>
              All states
            </MenuItem>
            {stateOptions.map((code) => (
              <MenuItem
                key={code}
                value={code}
                sx={{
                  fontSize: 13,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box component="span">{code}</Box>
                {CONTROL_STATES.has(code) ? <ControlChip /> : null}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {state ? (
          <Box
            component="button"
            type="button"
            onClick={() => selectState('')}
            sx={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              color: colors.textMuted,
              px: 0.5,
              transition: 'color 0.15s',
              '&:hover': { color: colors.chartPrimary },
            }}
          >
            Clear
          </Box>
        ) : null}
        {/* Legend — explains the "Control" mark on the options. */}
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, ml: 'auto' }}>
          <ControlChip />
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
            = ABC control state (state-run distribution)
          </Typography>
        </Box>
      </Box>

      {/* Tiles */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {isLoading || !data
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={108} />
            ))
          : mergedBuckets.map((b) => {
              const key = b.premises_type ?? 'other';
              return (
                <PremisesTile
                  key={key}
                  bucket={b}
                  breakdown={b.premises_type === 'OTHER' ? otherBreakdown : null}
                  online={b.premises_type === 'ONLINE' ? data.online_retailers : null}
                  selected={openBucket === key}
                  onSelect={() => setOpenBucket((cur) => (cur === key ? null : key))}
                />
              );
            })}
      </Box>

      {/* Account drill-down — the accounts behind whichever tile is
          expanded, so it's clear which accounts feed these numbers. */}
      {openBucket ? (
        <PremisesAccountsPanel
          bucket={openBucket}
          state={state || null}
          onClose={() => setOpenBucket(null)}
          onQueryChange={onPanelQueryChange}
        />
      ) : null}

      {/* Share bar — single horizontal stack showing volume distribution. */}
      {data && mergedBuckets.length > 0 && Number(data.grand_total_9l) > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden' }}>
            {mergedBuckets.map((b) => {
              const s = styleFor(b.premises_type);
              return (
                <Tooltip
                  key={b.premises_type ?? 'unknown'}
                  arrow
                  title={`${s.label} — ${(b.pct_of_9l * 100).toFixed(1)}% of volume`}
                  placement="top"
                >
                  <Box
                    sx={{
                      width: `${b.pct_of_9l * 100}%`,
                      bgcolor: s.color,
                      opacity: 0.85,
                      transition: 'opacity 0.15s',
                      '&:hover': { opacity: 1 },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
          <Typography
            sx={{
              fontSize: 11,
              color: colors.textMuted,
              mt: 0.5,
              fontStyle: 'italic',
            }}
          >
            Share of volume by premises type
          </Typography>
        </Box>
      ) : null}

      {/* Year-over-year comparison — pivots yearly_history into a
          grouped bar chart so reps can see whether each premises
          channel is growing, holding, or shrinking. The YTD year
          is marked so partial years don't read as apples-to-apples. */}
      {data && data.yearly_history.length > 0 ? (
        <PremisesYearlyChart yearly={data.yearly_history} />
      ) : null}
    </Paper>
  );
}

type PanelQuery = Pick<PremisesAccountsQuery, 'search' | 'sort' | 'activeOnly'>;

const PANEL_SORT_EXPORT_LABEL: Record<PremisesAccountSort, string> = {
  volume: 'Volume (high → low)',
  name: 'Name (A → Z)',
  recent: 'Recently active (newest → oldest)',
};

function buildPremisesExport(
  data: PremisesSummaryResponse,
  mergedBuckets: PremisesBucket[],
  drill: { bucket: string; accounts: PremisesAccount[] } | null,
  filters: ExportFilter[],
): ExcelWorkbookSpec {
  const sheets: ExcelWorkbookSpec['sheets'] = [
    excelSheet<PremisesBucket>({
      name: 'Premises Mix',
      rows: mergedBuckets,
      columns: [
        { header: 'Premises', value: (b) => styleFor(b.premises_type).label, width: 18 },
        { header: 'Accounts', value: (b) => b.account_count, format: 'int' },
        { header: '9L', value: (b) => num(b.total_9l), format: 'decimal' },
        { header: 'Share of 9L', value: (b) => b.pct_of_9l, format: 'percent', width: 12 },
      ],
    }),
  ];

  if (data.yearly_history.length > 0) {
    // Same NA + Unknown → Other merge as the yearly chart.
    const byKey = new Map<string, YearlyExportRow>();
    for (const cell of data.yearly_history) {
      const k = premisesKey(cell.premises_type);
      const id = `${k}|${cell.year}`;
      const row = byKey.get(id);
      if (row) {
        row.cases9l += Number(cell.cases_9l);
        row.isYtd = row.isYtd || cell.is_ytd;
        row.months = Math.max(row.months, cell.months_covered);
      } else {
        byKey.set(id, {
          key: k,
          year: cell.year,
          cases9l: Number(cell.cases_9l),
          isYtd: cell.is_ytd,
          months: cell.months_covered,
        });
      }
    }
    const rows = [...byKey.values()].sort(
      (a, b) => SERIES_ORDER.indexOf(a.key) - SERIES_ORDER.indexOf(b.key) || a.year - b.year,
    );
    sheets.push(
      excelSheet<YearlyExportRow>({
        name: 'By Year',
        rows,
        columns: [
          { header: 'Premises', value: (r) => SERIES_LABEL[r.key], width: 18 },
          { header: 'Year', value: (r) => r.year, width: 8 },
          { header: '9L', value: (r) => r.cases9l, format: 'decimal' },
          { header: 'Year to Date', value: (r) => (r.isYtd ? 'Yes' : 'No'), width: 13 },
          { header: 'Months Covered', value: (r) => r.months, format: 'int' },
        ],
      }),
    );
  }

  if (data.online_retailers.length > 0) {
    sheets.push(
      excelSheet<OnlineRetailerAccount>({
        name: 'Online Retailers',
        rows: data.online_retailers,
        columns: [
          { header: 'Account', value: (r) => r.name, width: 34 },
          { header: 'State', value: (r) => r.state_code, width: 7 },
          { header: '9L', value: (r) => num(r.total_9l), format: 'decimal' },
        ],
      }),
    );
  }

  if (drill) {
    sheets.push(
      excelSheet<PremisesAccount>({
        name: `${styleFor(drill.bucket).label} Accounts`,
        rows: drill.accounts,
        columns: [
          ...accountColumns<PremisesAccount>(),
          { header: 'Premises', value: (a) => premisesLabel(a.premises_type), width: 12 },
          { header: 'Raw Premises', value: (a) => a.raw_premises, width: 14 },
          { header: '9L', value: (a) => num(a.total_9l), format: 'decimal' },
          { header: 'Physical', value: (a) => num(a.cases_physical), format: 'decimal' },
          { header: 'Products', value: (a) => a.product_count, format: 'int' },
          { header: 'Carried', value: (a) => a.carried_products.join(', '), width: 40 },
          { header: 'Missing', value: (a) => a.missing_products.join(', '), width: 40 },
          {
            header: 'Last Active',
            value: (a) => isoDate(a.last_active_period),
            format: 'date',
          },
          {
            header: 'Share of Bucket',
            value: (a) => a.pct_of_bucket,
            format: 'percent',
            width: 14,
          },
        ],
      }),
    );
  }

  return { title: 'On- vs Off-Premise Mix', filters, sheets };
}

interface YearlyExportRow {
  key: SeriesKey;
  year: number;
  cases9l: number;
  isYtd: boolean;
  months: number;
}

interface OtherBreakdown {
  naAccts: number;
  naVol: number;
  unkAccts: number;
  unkVol: number;
}

interface PremisesTileProps {
  bucket: PremisesBucket;
  // Only set for the merged "Other" tile — drives the NA vs Unknown split.
  breakdown?: OtherBreakdown | null;
  // Only set for the "Online Retailers" tile — names the accounts inside.
  online?: OnlineRetailerAccount[] | null;
  // Whether this tile's account drill-down is currently open.
  selected?: boolean;
  // Click handler — toggles the account drill-down for this bucket.
  onSelect?: () => void;
}

function PremisesTile({ bucket, breakdown, online, selected, onSelect }: PremisesTileProps) {
  const s = styleFor(bucket.premises_type);
  const sharePct = (bucket.pct_of_9l * 100).toFixed(1);

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.25, maxWidth: 280 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: s.color, mb: 0.4 }}>
            {s.label}
          </Typography>
          <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.45 }}>
            {s.meaning}
          </Typography>
          {breakdown ? (
            <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px solid ${colors.borderLight}` }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  mb: 0.4,
                }}
              >
                What's inside
              </Typography>
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.6 }}>
                <Box component="span" sx={{ color: colors.warning, fontWeight: 700 }}>
                  NA
                </Box>{' '}
                (broker-classified) — {fmtInt.format(breakdown.naAccts)} accts ·{' '}
                {fmt6PkNumber(breakdown.naVol)} 6-pk
                <br />
                <Box component="span" sx={{ color: colors.textMuted, fontWeight: 700 }}>
                  Unknown
                </Box>{' '}
                (awaiting classification) — {fmtInt.format(breakdown.unkAccts)} accts ·{' '}
                {fmt6PkNumber(breakdown.unkVol)} 6-pk
              </Typography>
            </Box>
          ) : null}
          {online && online.length > 0 ? (
            <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px solid ${colors.borderLight}` }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  mb: 0.4,
                }}
              >
                Accounts inside
              </Typography>
              {online.map((r) => (
                <Box
                  key={`${r.name}-${r.state_code ?? ''}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    fontSize: 11,
                    color: colors.textSecondary,
                    lineHeight: 1.6,
                  }}
                >
                  <Box component="span">
                    {r.name}
                    {r.state_code ? (
                      <Box component="span" sx={{ color: colors.textMuted }}>
                        {' '}
                        · {r.state_code}
                      </Box>
                    ) : null}
                  </Box>
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap', fontFeatureSettings: '"tnum"' }}
                  >
                    {fmt6PkNumber(r.total_9l)} 6-pk
                  </Box>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            px: 1.5,
            py: 1.25,
            boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
            maxWidth: 320,
          },
        },
        arrow: { sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } } },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect?.();
          }
        }}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          border: `1px solid ${selected ? s.color : `${s.color}33`}`,
          boxShadow: selected ? `0 0 0 1px ${s.color}, 0 6px 16px ${s.color}22` : 'none',
          bgcolor: s.bg,
          p: 1.75,
          cursor: 'pointer',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          '&:hover': {
            borderColor: `${s.color}77`,
            boxShadow: selected
              ? `0 0 0 1px ${s.color}, 0 6px 16px ${s.color}22`
              : `0 4px 12px ${s.color}1f`,
          },
        }}
      >
        {/* Top row: icon + label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: '#fff',
              color: s.color,
              border: `1px solid ${s.color}33`,
            }}
          >
            {s.icon}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 700,
                color: s.color,
                lineHeight: 1.1,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {s.label}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: colors.textMuted, lineHeight: 1.2 }}>
              {s.description}
            </Typography>
          </Box>
        </Box>

        {/* Numbers */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color: colors.textPrimary,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              fontFeatureSettings: '"tnum"',
            }}
          >
            {fmtInt.format(bucket.account_count)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: colors.textMuted }}>accounts</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.75,
            mt: 0.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: s.color,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {fmt6PkNumber(bucket.total_9l)} 6-pk
          </Typography>
          <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
            · {sharePct}% share
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: 11,
            color: colors.textMuted,
            mt: 0.15,
            fontFeatureSettings: '"tnum"',
          }}
        >
          {fmt.format(Number(bucket.total_9l))} 9L
        </Typography>

        {/* NA vs Unknown split — makes the merged "Other" bucket
            transparent about what it contains. */}
        {breakdown ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              mt: 0.85,
              pt: 0.75,
              borderTop: `1px dashed ${s.color}33`,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colors.warning }} />
              <Typography sx={{ fontSize: 10.5, color: colors.textMuted }}>
                NA{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {fmtInt.format(breakdown.naAccts)}
                </Box>
              </Typography>
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colors.textMuted }} />
              <Typography sx={{ fontSize: 10.5, color: colors.textMuted }}>
                Unknown{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {fmtInt.format(breakdown.unkAccts)}
                </Box>
              </Typography>
            </Box>
          </Box>
        ) : null}

        {/* Click affordance — reveals the accounts behind this bucket. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.4,
            mt: 1,
            pt: 0.75,
            borderTop: `1px dashed ${s.color}33`,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            color: selected ? s.color : colors.textMuted,
          }}
        >
          {selected ? 'Hide accounts' : 'View accounts'}
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 15,
              transition: 'transform 0.15s',
              transform: selected ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
}

// ----------------------------------------------------------------
// Account drill-down panel
// ----------------------------------------------------------------

// "2026-07-01" -> "Jul '26". Compact last-active label.
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
function monthLabel(iso: string | null): string {
  if (!iso) return '—';
  const [y, m] = iso.split('-');
  const mi = Number(m) - 1;
  if (!y || mi < 0 || mi > 11) return '—';
  return `${MONTH_ABBR[mi]} '${y.slice(2)}`;
}

// A wrapped set of product chips (carried or missing), capped with a
// "+N" overflow that lists the rest on hover.
function ProductChips({
  items,
  accent,
  variant,
  emptyLabel,
  max = 4,
}: {
  items: string[];
  accent: string;
  variant: 'carry' | 'missing';
  emptyLabel: string;
  max?: number;
}) {
  if (items.length === 0) {
    return (
      <Typography sx={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
        {emptyLabel}
      </Typography>
    );
  }
  const shown = items.slice(0, max);
  const rest = items.slice(max);
  const chipSx =
    variant === 'carry'
      ? { color: accent, bgcolor: `${accent}14`, border: `1px solid ${accent}33` }
      : {
          color: colors.textMuted,
          bgcolor: 'transparent',
          border: `1px dashed ${colors.border}`,
        };
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {shown.map((label) => (
        <Box
          key={label}
          component="span"
          sx={{
            px: 0.6,
            py: 0.12,
            borderRadius: 1,
            fontSize: 10.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            ...chipSx,
          }}
        >
          {shortProductLabel(label)}
        </Box>
      ))}
      {rest.length > 0 ? (
        <Tooltip arrow title={rest.map(shortProductLabel).join(', ')}>
          <Box
            component="span"
            sx={{
              px: 0.6,
              py: 0.12,
              borderRadius: 1,
              fontSize: 10.5,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              cursor: 'default',
              ...chipSx,
            }}
          >
            +{rest.length}
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
}

function PremisesAccountsPanel({
  bucket,
  state,
  onClose,
  onQueryChange,
}: {
  bucket: string;
  state: string | null;
  onClose: () => void;
  onQueryChange: (q: PanelQuery | null) => void;
}) {
  const s = styleFor(bucket);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<PremisesAccountSort>('volume');
  const [activeOnly, setActiveOnly] = useState(false);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    onQueryChange({ search, sort, activeOnly });
    return () => onQueryChange(null);
  }, [onQueryChange, search, sort, activeOnly]);

  const { data, isLoading, error, isFetching } = useDepletionsPremisesAccounts(bucket, {
    state,
    search,
    sort,
    activeOnly,
    limit: 100,
  });

  const isOther = bucket === 'OTHER';

  return (
    <Box sx={{ mt: 2, borderRadius: 2, border: `1px solid ${s.color}44`, overflow: 'hidden' }}>
      {/* Header — title + count + close */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.25,
          bgcolor: s.bg,
          borderBottom: `1px solid ${s.color}33`,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Box sx={{ color: s.color, display: 'inline-flex' }}>{s.icon}</Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>
            Accounts in {s.label}
            {state ? (
              <Box component="span" sx={{ color: colors.textMuted, fontWeight: 600 }}>
                {' '}
                · {state}
              </Box>
            ) : null}
          </Typography>
          {data ? (
            <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
              {data.total_count > data.accounts.length
                ? `top ${data.accounts.length} of ${fmtInt.format(data.total_count)}`
                : `${fmtInt.format(data.total_count)} account${data.total_count === 1 ? '' : 's'}`}
              {search ? ' matching' : ''}
            </Typography>
          ) : null}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={onClose}
          sx={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            color: colors.textMuted,
            transition: 'color 0.15s',
            '&:hover': { color: s.color },
          }}
        >
          Close
        </Box>
      </Box>

      {/* Controls — search + sort + active-only */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${colors.borderLight}`,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
          <SearchIcon
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 17,
              color: colors.textMuted,
              pointerEvents: 'none',
            }}
          />
          <InputBase
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search account or city…"
            sx={{
              width: '100%',
              fontSize: 13,
              pl: 4,
              pr: 1,
              py: 0.4,
              bgcolor: '#fff',
              border: `1px solid ${colors.border}`,
              borderRadius: 1.5,
              '&.Mui-focused': { borderColor: s.color },
            }}
          />
        </Box>
        <FormControl size="small">
          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value as PremisesAccountSort)}
            aria-label="Sort accounts"
            sx={{
              fontSize: 12.5,
              bgcolor: '#fff',
              '& .MuiSelect-select': { py: 0.5 },
              '& fieldset': { borderColor: colors.border },
            }}
          >
            <MenuItem value="volume" sx={{ fontSize: 12.5 }}>
              Sort: Volume
            </MenuItem>
            <MenuItem value="name" sx={{ fontSize: 12.5 }}>
              Sort: Name (A–Z)
            </MenuItem>
            <MenuItem value="recent" sx={{ fontSize: 12.5 }}>
              Sort: Recently active
            </MenuItem>
          </Select>
        </FormControl>
        <Box
          component="button"
          type="button"
          onClick={() => setActiveOnly((v) => !v)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: 999,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            border: `1px solid ${activeOnly ? s.color : colors.border}`,
            bgcolor: activeOnly ? `${s.color}12` : '#fff',
            color: activeOnly ? s.color : colors.textMuted,
            transition: 'all 0.15s',
          }}
        >
          {activeOnly ? '✓ ' : ''}Active only
        </Box>
        {isFetching ? (
          <Typography sx={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
            updating…
          </Typography>
        ) : null}
      </Box>

      {/* Body — a table so the columns fill the width evenly. */}
      <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
        {error ? (
          <Typography sx={{ p: 2, fontSize: 12.5, color: colors.error }}>
            Failed to load accounts.
          </Typography>
        ) : isLoading || !data ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={34} />
            ))}
          </Box>
        ) : data.accounts.length === 0 ? (
          <Typography sx={{ p: 2, fontSize: 12.5, color: colors.textMuted }}>
            {search
              ? `No accounts match “${search}”.`
              : 'No accounts in this bucket for the current filter.'}
          </Typography>
        ) : (
          <>
            {/* Column header — sticks while the rows scroll. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 0.75,
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: colors.surfaceSubtle,
                borderBottom: `1px solid ${colors.borderLight}`,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: colors.textMuted,
              }}
            >
              <Box sx={{ width: 24, flexShrink: 0, textAlign: 'right' }}>#</Box>
              <Box sx={{ flex: '1 1 150px', minWidth: 130 }}>Account</Box>
              <Box
                sx={{ flex: '1.4 1 150px', minWidth: 130, display: { xs: 'none', sm: 'block' } }}
              >
                Carries
              </Box>
              <Box
                sx={{ flex: '1.4 1 150px', minWidth: 130, display: { xs: 'none', md: 'block' } }}
              >
                Missing (white-space)
              </Box>
              <Box sx={{ width: 128, flexShrink: 0, textAlign: 'right' }}>Volume</Box>
            </Box>

            {data.accounts.map((a, i) => (
              <Box
                key={a.account_id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 0.85,
                  borderTop: `1px solid ${colors.borderLight}`,
                  transition: 'background 0.12s',
                  '&:hover': { bgcolor: `${s.color}08` },
                }}
              >
                {/* Rank */}
                <Typography
                  sx={{
                    fontSize: 11,
                    color: colors.textMuted,
                    width: 24,
                    flexShrink: 0,
                    textAlign: 'right',
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {i + 1}
                </Typography>

                {/* Account name + location + last active */}
                <Box sx={{ flex: '1 1 150px', minWidth: 130 }}>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: colors.textPrimary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {a.name}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.6,
                      fontSize: 11,
                      color: colors.textMuted,
                      flexWrap: 'wrap',
                    }}
                  >
                    {isOther ? (
                      <Box
                        component="span"
                        sx={{
                          px: 0.5,
                          py: 0.05,
                          borderRadius: 0.75,
                          fontSize: 9.5,
                          fontWeight: 700,
                          color: a.raw_premises === 'NA' ? colors.warning : colors.textMuted,
                          bgcolor:
                            a.raw_premises === 'NA' ? `${colors.warning}18` : colors.surfaceSubtle,
                          border: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        {a.raw_premises === 'NA' ? 'NA' : 'Unknown'}
                      </Box>
                    ) : null}
                    <Box component="span">
                      {[a.city, a.state_code].filter(Boolean).join(', ') || '—'}
                    </Box>
                    <Box component="span" sx={{ color: colors.borderLight }}>
                      ·
                    </Box>
                    <Box component="span">last {monthLabel(a.last_active_period)}</Box>
                  </Box>
                </Box>

                {/* Carries — products this account buys */}
                <Box
                  sx={{ flex: '1.4 1 150px', minWidth: 130, display: { xs: 'none', sm: 'block' } }}
                >
                  <ProductChips
                    items={a.carried_products}
                    accent={s.color}
                    variant="carry"
                    emptyLabel="none yet"
                    max={5}
                  />
                </Box>

                {/* Missing — catalog gaps (best opportunity first) */}
                <Box
                  sx={{ flex: '1.4 1 150px', minWidth: 130, display: { xs: 'none', md: 'block' } }}
                >
                  <ProductChips
                    items={a.missing_products}
                    accent={s.color}
                    variant="missing"
                    emptyLabel="carries all"
                    max={4}
                  />
                </Box>

                {/* Volume */}
                <Box sx={{ width: 128, flexShrink: 0, textAlign: 'right' }}>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {fmt6PkNumber(a.total_9l)} 6-pk
                  </Typography>
                  <Typography
                    sx={{ fontSize: 10.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}
                  >
                    {(a.pct_of_bucket * 100).toFixed(1)}% · {fmt.format(Number(a.total_9l))} 9L
                  </Typography>
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------
// Year-over-year comparison chart
// ----------------------------------------------------------------

// Display order for the three series — controls legend + bar order.
// NA + Unknown are collapsed into a single "Other" series.
const SERIES_ORDER = ['OFF', 'ON', 'OTHER', 'ONLINE'] as const;
type SeriesKey = (typeof SERIES_ORDER)[number];

function premisesKey(t: string | null): SeriesKey {
  if (t === 'OFF') return 'OFF';
  if (t === 'ON') return 'ON';
  if (t === 'ONLINE') return 'ONLINE';
  return 'OTHER';
}

const SERIES_LABEL: Record<SeriesKey, string> = {
  OFF: 'Off-Premise',
  ON: 'On-Premise',
  OTHER: 'Other',
  ONLINE: 'Online Retailers',
};

const SERIES_COLOR: Record<SeriesKey, string> = {
  OFF: colors.info,
  ON: colors.success,
  OTHER: colors.warning,
  ONLINE: ONLINE_COLOR,
};

// One cell in the long-format response — but flattened to numbers
// and grouped by category for the small-multiples layout.
interface YearCell {
  year: number;
  cases_9l: number;
  is_ytd: boolean;
  months: number;
}

interface PremisesYearlyChartProps {
  yearly: PremisesYearlyBucket[];
}

// Compact 9L formatter — keeps headlines tight (e.g. "1.8K", "267").
function fmtCases(v: number): string {
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toFixed(0);
}

function PremisesYearlyChart({ yearly }: PremisesYearlyChartProps) {
  // Group the long-format response by category. Each entry becomes
  // ON / OFF / NA / UNKNOWN -> list of (year, cases_9l, is_ytd, months).
  // Local Y-axis per panel is the whole point of small multiples —
  // Unknown's 8 → 20 reads just as clearly as OFF's 1.2K → 1.8K.
  const byCategory = useMemo(() => {
    // Accumulate per (category, year) so the merged "Other" series sums
    // NA and Unknown that share the same year into a single bar.
    const maps: Record<SeriesKey, Map<number, YearCell>> = {
      OFF: new Map(),
      ON: new Map(),
      OTHER: new Map(),
      ONLINE: new Map(),
    };
    for (const cell of yearly) {
      const k = premisesKey(cell.premises_type);
      const existing = maps[k].get(cell.year);
      if (existing) {
        existing.cases_9l += Number(cell.cases_9l);
        existing.is_ytd = existing.is_ytd || cell.is_ytd;
        existing.months = Math.max(existing.months, cell.months_covered);
      } else {
        maps[k].set(cell.year, {
          year: cell.year,
          cases_9l: Number(cell.cases_9l),
          is_ytd: cell.is_ytd,
          months: cell.months_covered,
        });
      }
    }
    const cats: Record<SeriesKey, YearCell[]> = { OFF: [], ON: [], OTHER: [], ONLINE: [] };
    for (const k of SERIES_ORDER) {
      cats[k] = Array.from(maps[k].values()).sort((a, b) => a.year - b.year);
    }
    return cats;
  }, [yearly]);

  // Grand total across all categories and years — denominator for
  // each panel's share-of-mix headline.
  const grandTotal = useMemo(
    () =>
      SERIES_ORDER.reduce((acc, k) => acc + byCategory[k].reduce((s, y) => s + y.cases_9l, 0), 0),
    [byCategory],
  );

  // Narrative line — names the YoY winner / loser across categories
  // so a rep gets the story in one glance before reading the panels.
  const narrative = useMemo(() => {
    const stories: { label: string; pct: number; color: string }[] = [];
    for (const k of SERIES_ORDER) {
      const fullYears = byCategory[k].filter((y) => !y.is_ytd);
      if (fullYears.length < 2) continue;
      const prev = fullYears[fullYears.length - 2]!.cases_9l;
      const curr = fullYears[fullYears.length - 1]!.cases_9l;
      if (prev <= 0) continue;
      const pct = ((curr - prev) / prev) * 100;
      stories.push({ label: SERIES_LABEL[k], pct, color: SERIES_COLOR[k] });
    }
    if (stories.length === 0) return null;
    stories.sort((a, b) => b.pct - a.pct);
    return stories;
  }, [byCategory]);

  return (
    <Box
      sx={{
        mt: 2.5,
        p: 2.25,
        borderRadius: 2,
        bgcolor: colors.surfaceSubtle,
        border: `1px solid ${colors.borderLight}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 0.75,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              mb: 0.4,
            }}
          >
            Year-over-year
          </Typography>
          <Typography
            sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 }}
          >
            Channel trajectory by premises type
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
          Each panel uses its own scale. YTD year is shown with a lighter bar.
        </Typography>
      </Box>

      {/* Narrative summary — names the YoY winner / loser so a rep
          gets the story in a sentence before reading any panels. */}
      {narrative && narrative.length > 0 && (
        <Typography
          sx={{
            fontSize: 12,
            color: colors.textSecondary,
            mb: 1.5,
            lineHeight: 1.5,
          }}
        >
          {narrative.map((s, i) => (
            <Box component="span" key={s.label} sx={{ whiteSpace: 'nowrap' }}>
              {i > 0 && (
                <Box component="span" sx={{ color: colors.textMuted, mx: 0.6 }}>
                  ·
                </Box>
              )}
              <Box component="span" sx={{ fontWeight: 700, color: s.color }}>
                {s.label}
              </Box>{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: s.pct >= 0 ? colors.success : colors.error,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {s.pct >= 0 ? '+' : ''}
                {s.pct.toFixed(0)}%
              </Box>
            </Box>
          ))}{' '}
          <Box component="span" sx={{ color: colors.textMuted, fontStyle: 'italic' }}>
            YoY (last full year vs prior)
          </Box>
        </Typography>
      )}

      {/* Pace definition — always visible so "pace" is never a mystery.
          Each YTD panel then repeats the detail on hover. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 0.5,
          mb: 1.5,
          px: 1.25,
          py: 0.9,
          borderRadius: 1.5,
          bgcolor: '#fff',
          border: `1px solid ${colors.borderLight}`,
          fontSize: 11.5,
          color: colors.textSecondary,
          lineHeight: 1.5,
        }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 800, color: colors.textPrimary, whiteSpace: 'nowrap' }}
        >
          ↗ Pace
        </Box>
        <Box component="span">
          = projected full-year volume if the current year-to-date rate holds through December (YTD
          volume ÷ months reported × 12). It's a projection, not a booked figure.
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {SERIES_ORDER.map((k) => (
          <PremisesPanel key={k} category={k} years={byCategory[k]} grandTotal={grandTotal} />
        ))}
      </Box>

      {/* Reader's guide — collapsed by default, expands inline. The
          row content is bespoke to the YoY chart's visual language
          (solid bars, striped bars, dashed ghost projection, etc.). */}
      <SectionExplainer
        rows={[
          {
            sample: <SampleSolidBar />,
            term: 'Solid bar',
            desc: (
              <>
                A complete calendar year — like <strong>2024</strong> or <strong>2025</strong>.
              </>
            ),
          },
          {
            sample: <SampleStripedBar />,
            term: 'Striped bar',
            desc: (
              <>
                Year-to-date — only the months reported so far. The number above the bar is what
                we've actually shipped; the <strong>“6 mo”</strong> tag below it tells you how many
                months that covers.
              </>
            ),
          },
          {
            sample: <SampleGhostBar />,
            term: 'Dashed ghost + ↗ pace',
            desc: (
              <>
                Stacks on top of the YTD bar. The <strong>↗ pace 3.0K</strong> callout beside it is
                the projected full-year volume if today's rate holds through December; the{' '}
                <strong>actual</strong> callout beside the solid bar is what's actually shipped so
                far.
              </>
            ),
          },
          {
            sample: <SamplePeak />,
            term: '▲ marker',
            desc: (
              <>
                Highlights the year with the highest volume — the channel's <strong>peak</strong> so
                far.
              </>
            ),
          },
          {
            sample: <SampleChip tone="success" text="+57%" />,
            term: 'YoY chip',
            desc: (
              <>
                Closed years only — last full year vs the year before. Tells you how the channel
                ended the previous year.
              </>
            ),
          },
          {
            sample: <SampleChip tone="error" text="-23%" />,
            term: "'26 PACE · vs '25 / vs '24",
            desc: (
              <>
                Current year projected forward to 12 months, then compared to each prior year —
                quickly answers <em>“are we ahead or behind?”</em>
              </>
            ),
          },
        ]}
      />
    </Box>
  );
}

const SAMPLE_COLOR = colors.info; // neutral OFF-Premise blue for the samples

function SampleSolidBar() {
  return (
    <Box
      sx={{
        width: 12,
        height: 26,
        borderRadius: '3px 3px 0 0',
        background: `linear-gradient(180deg, ${SAMPLE_COLOR}ee 0%, ${SAMPLE_COLOR} 100%)`,
        boxShadow: `inset 0 -1px 0 ${SAMPLE_COLOR}`,
      }}
    />
  );
}

function SampleStripedBar() {
  return (
    <Box
      sx={{
        width: 12,
        height: 26,
        borderRadius: '3px 3px 0 0',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${SAMPLE_COLOR}88 0%, ${SAMPLE_COLOR}b3 100%)`,
        border: `1px dashed ${SAMPLE_COLOR}77`,
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(135deg, rgba(255,255,255,0.55) 0 2px, transparent 2px 8px)',
        },
      }}
    />
  );
}

function SampleGhostBar() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '3px 3px 0 0',
          border: `1px dashed ${SAMPLE_COLOR}77`,
          borderBottom: 'none',
          background: `repeating-linear-gradient(135deg, ${SAMPLE_COLOR}1a 0 3px, transparent 3px 7px)`,
        }}
      />
      <Box
        sx={{
          width: 12,
          height: 14,
          background: `linear-gradient(180deg, ${SAMPLE_COLOR}aa 0%, ${SAMPLE_COLOR} 100%)`,
        }}
      />
    </Box>
  );
}

function SamplePeak() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 18,
        lineHeight: 1,
        color: SAMPLE_COLOR,
        fontWeight: 800,
      }}
    >
      ▲
    </Box>
  );
}

interface SampleChipProps {
  tone: 'success' | 'error';
  text: string;
}

function SampleChip({ tone, text }: SampleChipProps) {
  const c = tone === 'success' ? colors.success : colors.error;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 800,
        color: c,
        fontFeatureSettings: '"tnum"',
        px: 0.55,
        py: 0.1,
        borderRadius: 0.75,
        bgcolor: `${c}14`,
        border: `1px solid ${c}40`,
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </Box>
  );
}

// ----------------------------------------------------------------
// Single per-category panel — eyebrow + 3 bars + YoY chip
// ----------------------------------------------------------------

interface PremisesPanelProps {
  category: SeriesKey;
  years: YearCell[];
  grandTotal: number;
}

function PremisesPanel({ category, years, grandTotal }: PremisesPanelProps) {
  const color = SERIES_COLOR[category];
  const label = SERIES_LABEL[category];
  const categoryTotal = years.reduce((s, y) => s + y.cases_9l, 0);
  const sharePct = grandTotal > 0 ? (categoryTotal / grandTotal) * 100 : 0;

  // YoY: last full year vs the year before. Skip YTD so we're not
  // pitting 6 months against 12.
  const fullYears = years.filter((y) => !y.is_ytd);
  const yoyPct =
    fullYears.length >= 2 && fullYears[fullYears.length - 2]!.cases_9l > 0
      ? ((fullYears[fullYears.length - 1]!.cases_9l - fullYears[fullYears.length - 2]!.cases_9l) /
          fullYears[fullYears.length - 2]!.cases_9l) *
        100
      : null;

  // YTD analysis — if the latest cell is partial, project it forward
  // to a full-year pace and compare against each prior full year so
  // reps can see whether the channel is recovering or accelerating
  // its trend (vs most-recent year) AND how it's holding up against
  // the multi-year base (vs first year on record).
  const ytdCell = years.find((y) => y.is_ytd);
  const annualizedYtd =
    ytdCell && ytdCell.months > 0 && ytdCell.months < 12
      ? ytdCell.cases_9l * (12 / ytdCell.months)
      : null;
  const lastFullYear = fullYears.length > 0 ? fullYears[fullYears.length - 1]! : null;
  const firstFullYear = fullYears.length > 0 ? fullYears[0]! : null;
  const pacingPctVsLast =
    annualizedYtd !== null && lastFullYear && lastFullYear.cases_9l > 0
      ? ((annualizedYtd - lastFullYear.cases_9l) / lastFullYear.cases_9l) * 100
      : null;
  const pacingPctVsFirst =
    annualizedYtd !== null &&
    firstFullYear &&
    firstFullYear.cases_9l > 0 &&
    firstFullYear !== lastFullYear
      ? ((annualizedYtd - firstFullYear.cases_9l) / firstFullYear.cases_9l) * 100
      : null;

  // Peak year — highlights the strongest year for this channel.
  const peakYear = years.reduce(
    (best, y) => (y.cases_9l > best.cases_9l ? y : best),
    years[0] ?? { year: 0, cases_9l: 0, is_ytd: false, months: 0 },
  );

  // Scale anchors the visual to whichever is larger: actual values or
  // the projected ghost bar. Otherwise the projection clips above the
  // chart area when pacing exceeds the previous peak.
  const maxVal = Math.max(...years.map((y) => y.cases_9l), annualizedYtd ?? 0, 1);
  // Three light gridlines for visual rhythm — 25 / 50 / 75 % of max.
  const gridFractions = [0.25, 0.5, 0.75];

  // YTD bars get a CSS diagonal-stripe overlay so partial periods are
  // visually distinct beyond color/opacity alone.
  const ytdStripes =
    'repeating-linear-gradient(135deg, rgba(255,255,255,0.55) 0 2px, transparent 2px 8px)';

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 2,
        p: 1.75,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': {
          borderColor: `${color}55`,
          boxShadow: `0 6px 18px ${color}1f`,
        },
      }}
    >
      {/* Eyebrow: colored dot + category + share */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1.25 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: color,
            flexShrink: 0,
            boxShadow: `0 0 0 3px ${color}1a`,
          }}
        />
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            color: colors.textMuted,
            ml: 'auto',
            fontFeatureSettings: '"tnum"',
          }}
        >
          {sharePct.toFixed(1)}% of mix
        </Typography>
      </Box>

      {/* Bars — flex layout so DOM-measured widths give crisp bars
          at any container size. Each bar is its own column with the
          value label above and the year label below. */}
      <Box
        sx={{
          position: 'relative',
          height: 132,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: 3,
          px: 0.5,
          // Background gridlines.
          backgroundImage: gridFractions
            .map(() => `linear-gradient(${colors.borderLight}, ${colors.borderLight})`)
            .join(', '),
          backgroundSize: gridFractions.map(() => '100% 1px').join(', '),
          backgroundPosition: gridFractions.map((f) => `0 ${(1 - f) * 100}%`).join(', '),
          backgroundRepeat: 'no-repeat',
          // Solid baseline rule at the bottom for visual closure.
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        {/* Scale anchor — names what the top of the chart represents.
            Subtle so it doesn't compete with bar labels. */}
        <Typography
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            fontSize: 9,
            fontWeight: 600,
            color: colors.textMuted,
            fontFeatureSettings: '"tnum"',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            pointerEvents: 'none',
          }}
        >
          max {fmtCases(toSixPacks(maxVal))}{' '}
          <Box component="span" sx={{ textTransform: 'none' }}>
            6-pk
          </Box>
        </Typography>
        {years.map((yr) => {
          const heightPct = (yr.cases_9l / maxVal) * 100;
          // For YTD years, also compute the annualized projection
          // height so we can stack a ghost bar on top of the actual.
          const projectionPct =
            yr.is_ytd && annualizedYtd && annualizedYtd > yr.cases_9l
              ? (annualizedYtd / maxVal) * 100
              : null;
          const ghostHeightPct =
            projectionPct !== null ? Math.max(projectionPct - heightPct, 0) : 0;
          const isPeak = peakYear.year === yr.year && peakYear.cases_9l > 0;
          // A YTD column that projects above its actual gets the
          // "pace on top, projection in the middle" treatment.
          const isYtdProjection = ghostHeightPct > 0 && annualizedYtd !== null;

          // Shared solid bar — the actual reported volume. YTD years
          // keep the dashed/striped treatment; full years are solid.
          // `inner` lets the YTD bar carry its actual value inside it.
          const renderSolidBar = (inner?: ReactNode) => (
            <Box
              sx={{
                width: '100%',
                height: `${Math.max(heightPct, 2)}%`,
                borderRadius: ghostHeightPct > 0 ? '0 0 0 0' : '3px 3px 0 0',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: yr.is_ytd
                  ? `linear-gradient(180deg, ${color}88 0%, ${color}b3 100%)`
                  : `linear-gradient(180deg, ${color}ee 0%, ${color} 100%)`,
                boxShadow: yr.is_ytd
                  ? 'none'
                  : `inset 0 -1px 0 ${color}, 0 1px 2px rgba(15,23,42,0.05)`,
                border: yr.is_ytd ? `1px dashed ${color}77` : 'none',
                borderTop: ghostHeightPct > 0 ? 'none' : undefined,
                transition: 'transform 0.15s, filter 0.15s',
                '&:hover': {
                  filter: 'brightness(1.05)',
                  transform: 'translateY(-1px)',
                },
                // Stripe overlay for YTD bars.
                '&::after': yr.is_ytd
                  ? {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: ytdStripes,
                      pointerEvents: 'none',
                    }
                  : undefined,
              }}
            >
              {inner}
            </Box>
          );

          return (
            <Box
              key={yr.year}
              sx={{
                width: 22,
                flexShrink: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                position: 'relative',
              }}
            >
              {isYtdProjection ? (
                <>
                  {/* Pace callout — floated into the open space to the
                      right of the (last) YTD column, vertically centered
                      on the ghost so the projected total reads clearly
                      beside the dashed projection. Hover explains what
                      "pace" means. */}
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Box sx={{ p: 0.25, maxWidth: 240 }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color, mb: 0.4 }}>
                          Pace — projected full year
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.45 }}
                        >
                          Where {label} lands by December if the current year-to-date rate holds —
                          the {ytdCell?.months ?? 0}-month actual scaled up to 12 months (YTD ÷
                          months × 12). A projection, not a booked number.
                        </Typography>
                      </Box>
                    }
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: '#fff',
                          color: colors.textPrimary,
                          border: `1px solid ${colors.border}`,
                          borderRadius: 1.5,
                          px: 1.5,
                          py: 1.25,
                          boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
                          maxWidth: 280,
                        },
                      },
                      arrow: {
                        sx: {
                          color: '#fff',
                          '&::before': { border: `1px solid ${colors.border}` },
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '100%',
                        top: `${Math.max(100 - (heightPct + (projectionPct ?? 0)) / 2, 0)}%`,
                        transform: 'translateY(-50%)',
                        ml: 0.75,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        lineHeight: 1.05,
                        color,
                        whiteSpace: 'nowrap',
                        cursor: 'help',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: 8,
                          fontWeight: 800,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          opacity: 0.9,
                          borderBottom: `1px dotted ${color}99`,
                        }}
                      >
                        ↗ pace
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 12,
                          fontWeight: 800,
                          fontFeatureSettings: '"tnum"',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {fmtCases(toSixPacks(annualizedYtd))}
                      </Box>
                    </Box>
                  </Tooltip>
                  {/* Dashed ghost — the projected extension above the
                      actual. The value lives in the callout to the side. */}
                  <Box
                    sx={{
                      width: '100%',
                      height: `${ghostHeightPct}%`,
                      borderRadius: '3px 3px 0 0',
                      border: `1px dashed ${color}77`,
                      borderBottom: 'none',
                      background: `repeating-linear-gradient(135deg, ${color}1a 0 4px, transparent 4px 10px)`,
                      boxSizing: 'border-box',
                    }}
                  />
                  {renderSolidBar()}
                  {/* Actual callout — shipped-so-far total, beside the
                      solid bar so the number reads clearly instead of
                      being cramped inside a striped bar. */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '100%',
                      top: `${Math.max(100 - heightPct / 2, 0)}%`,
                      transform: 'translateY(-50%)',
                      ml: 0.75,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      lineHeight: 1.05,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: 8,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: colors.textMuted,
                      }}
                    >
                      actual
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        fontFeatureSettings: '"tnum"',
                        letterSpacing: '-0.01em',
                        color: colors.textPrimary,
                      }}
                    >
                      {fmtCases(toSixPacks(yr.cases_9l))}
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  {/* Value label above the bar — full years, plus any
                      YTD year that isn't pacing above its actual. */}
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color,
                      fontFeatureSettings: '"tnum"',
                      lineHeight: 1.2,
                      mb: 0.4,
                      whiteSpace: 'nowrap',
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {isPeak && (
                      <Box component="span" sx={{ fontSize: 9, mr: 0.2, opacity: 0.95 }}>
                        ▲
                      </Box>
                    )}
                    {fmtCases(toSixPacks(yr.cases_9l))}
                  </Typography>
                  {renderSolidBar()}
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Year labels under each column. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          px: 0.5,
          mt: 0.5,
        }}
      >
        {years.map((yr) => (
          <Box
            key={yr.year}
            sx={{
              width: 56,
              flexShrink: 0,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: yr.is_ytd ? colors.textMuted : colors.textSecondary,
                fontFeatureSettings: '"tnum"',
                letterSpacing: '0.04em',
                lineHeight: 1.15,
              }}
            >
              {yr.is_ytd ? `YTD '${String(yr.year).slice(-2)}` : `'${String(yr.year).slice(-2)}`}
            </Typography>
            {yr.is_ytd && yr.months > 0 && yr.months < 12 && (
              <Typography
                sx={{
                  fontSize: 8.5,
                  color: colors.textMuted,
                  fontFeatureSettings: '"tnum"',
                  lineHeight: 1.2,
                  mt: 0.1,
                }}
              >
                {yr.months} mo
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* 9L line — all years in one tidy row */}
      <Typography
        sx={{
          fontSize: 10.5,
          color: colors.textMuted,
          fontFeatureSettings: '"tnum"',
          mt: 1,
          textAlign: 'center',
        }}
      >
        {years.map((y, i) => (
          <Box component="span" key={y.year}>
            {i > 0 && (
              <Box component="span" sx={{ mx: 0.4 }}>
                ·
              </Box>
            )}
            <Box component="span" sx={{ fontWeight: 600, color: colors.textSecondary }}>
              {fmt.format(Number(y.cases_9l))}
            </Box>
          </Box>
        ))}{' '}
        9L
      </Typography>

      {/* Analysis section — YoY (closed years) + pacing (open YTD)
          with deltas against each prior full year. */}
      {(yoyPct !== null || annualizedYtd !== null) && (
        <Box
          sx={{
            mt: 0.75,
            pt: 0.75,
            borderTop: `1px dashed ${colors.borderLight}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.4,
          }}
        >
          {yoyPct !== null && fullYears.length >= 2 && (
            <AnalysisRow
              label={`YoY '${String(fullYears[fullYears.length - 2]!.year).slice(-2)} → '${String(fullYears[fullYears.length - 1]!.year).slice(-2)}`}
              valuePct={yoyPct}
            />
          )}
          {annualizedYtd !== null && ytdCell && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5,
                  mt: yoyPct !== null ? 0.3 : 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: colors.textMuted,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  '{String(ytdCell.year).slice(-2)} pace
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: colors.textPrimary,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {fmt6PkNumber(annualizedYtd)} 6-pk
                  <Box
                    component="span"
                    sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.4, fontSize: 10 }}
                  >
                    · {fmt.format(annualizedYtd)} 9L
                  </Box>
                </Typography>
              </Box>
              {pacingPctVsLast !== null && lastFullYear && (
                <AnalysisRow
                  label={`vs '${String(lastFullYear.year).slice(-2)}`}
                  valuePct={pacingPctVsLast}
                  indented
                />
              )}
              {pacingPctVsFirst !== null && firstFullYear && (
                <AnalysisRow
                  label={`vs '${String(firstFullYear.year).slice(-2)}`}
                  valuePct={pacingPctVsFirst}
                  indented
                />
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// AnalysisRow — small label-on-left, chip-on-right line used inside
// every premises panel for YoY + pacing deltas.
// ----------------------------------------------------------------

interface AnalysisRowProps {
  label: string;
  valuePct: number;
  indented?: boolean;
}

function AnalysisRow({ label, valuePct, indented }: AnalysisRowProps) {
  const positive = valuePct >= 0;
  const tone = positive ? colors.success : colors.error;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.5,
        pl: indented ? 1 : 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 9.5,
          fontWeight: indented ? 600 : 700,
          color: colors.textMuted,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {indented && (
          <Box component="span" sx={{ mr: 0.4, color: colors.borderLight }}>
            ↳
          </Box>
        )}
        {label}
      </Typography>
      <Box
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 800,
          color: tone,
          fontFeatureSettings: '"tnum"',
          px: 0.65,
          py: 0.15,
          borderRadius: 0.75,
          bgcolor: `${tone}14`,
          border: `1px solid ${tone}40`,
        }}
      >
        {positive ? '+' : ''}
        {valuePct.toFixed(0)}%
      </Box>
    </Box>
  );
}
